import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase,
  faBuilding,
  faCalendarAlt,
  faCalendarCheck,
  faClock,
  faGraduationCap,
  faMapMarkerAlt,
  faMapPin,
  faPuzzlePiece,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import dayjs from 'dayjs';
import { t } from '@lingui/macro';
import Job from '../types/job';
import { StateContext } from '../../state/contexts';

export type Properties = {
  job: Job;
  withIcon?: boolean;
  icon?: IconDefinition;
};

export type FormFieldProperties = {
  job: Job;
  withIcon?: boolean;
  icon?: IconDefinition;
  internalName?: string;
};

const JobCategoryList = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;
  const { enabled } = config.config.cardContent.categories;

  return (
    <>
      {enabled && job.jobOpening.categories?.length > 0 && (
        <ul className="dvinci-list-inline dvinci-list-categories">
          {(withIcon || showIcons) && (
            <li className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faBriefcase} fixedWidth />
            </li>
          )}
          {job.jobOpening.categories.map((category) => (
            <li key={category.id}>{category.name}</li>
          ))}
        </ul>
      )}
    </>
  );
};

const JobLocationList = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;
  const { enabled } = config.config.cardContent.locations;

  return (
    <>
      {enabled && job.jobOpening.locations?.length > 0 && (
        <ul className="dvinci-list-inline dvinci-list-locations">
          {(withIcon || showIcons) && (
            <li className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faMapMarkerAlt} fixedWidth />
            </li>
          )}
          {job.jobOpening.locations.map((location) => (
            <li key={location.id}>{location.name}</li>
          ))}
        </ul>
      )}
    </>
  );
};

const JobTargetGroupList = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;
  const { enabled } = config.config.cardContent.targetGroups;

  return (
    <>
      {enabled && job.jobOpening.targetGroups?.length > 0 && (
        <ul className="dvinci-list-inline dvinci-list-target-groups">
          {(withIcon || showIcons) && (
            <li className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faGraduationCap} fixedWidth />
            </li>
          )}
          {job.jobOpening.targetGroups.map((targetGroup) => (
            <li key={targetGroup.internalName}>{targetGroup.name}</li>
          ))}
        </ul>
      )}
    </>
  );
};

const JobWorkingTimeList = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;
  const { enabled } = config.config.cardContent.workingTimes;

  return (
    <>
      {enabled && job.jobOpening.workingTimes?.length > 0 && (
        <ul className="dvinci-list-inline dvinci-list-working-times">
          {(withIcon || showIcons) && (
            <li className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faClock} fixedWidth />
            </li>
          )}
          {job.jobOpening.workingTimes.map((workingTimes) => (
            <li key={workingTimes.internalName}>{workingTimes.name}</li>
          ))}
        </ul>
      )}
    </>
  );
};

const JobCompany = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;
  const { enabled } = config.config.cardContent.company;

  return (
    <>
      {enabled && job.jobOpening.company && (
        <div className="dvinci-job-property dvinci-job-company">
          {(withIcon || showIcons) && (
            <span className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faBuilding} fixedWidth />
            </span>
          )}
          {job.jobOpening.company.name}
        </div>
      )}
    </>
  );
};

const JobEarliestEntryDate = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;
  const { enabled } = config.config.cardContent.earliestEntryDate;

  return (
    <>
      {enabled && job.jobOpening.earliestEntryDate && (
        <div className="dvinci-job-property dvinci-job-earliest-entry-date">
          {(withIcon || showIcons) && (
            <span className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faCalendarCheck} fixedWidth />
            </span>
          )}
          {job.jobOpening.earliestEntryDate}
        </div>
      )}
    </>
  );
};

const JobEndDate = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;

  return (
    <>
      {job.endDate && (
        <div className="dvinci-job-property dvinci-job-end-date">
          {(withIcon || showIcons) && (
            <span className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faCalendarAlt} fixedWidth />
            </span>
          )}
          {job.endDate ? dayjs(job.endDate).locale('de').format('DD.MM.YYYY') : undefined}
        </div>
      )}
    </>
  );
};

const JobStartDate = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;

  return (
    <>
      {job.startDate && (
        <div className="dvinci-job-property dvinci-job-start-date">
          {(withIcon || showIcons) && (
            <span className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faCalendarAlt} fixedWidth />
            </span>
          )}
          {job.startDate ? dayjs(job.startDate).locale('de').format('DD.MM.YYYY') : undefined}
        </div>
      )}
    </>
  );
};

const JobContractPeriod = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;
  const { enabled } = config.config.cardContent.contractPeriod;

  return (
    <>
      {enabled && job.jobOpening.contractPeriod && (
        <div className="dvinci-job-property dvinci-job-contract-period">
          {(withIcon || showIcons) && (
            <span className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faCalendarAlt} fixedWidth />
            </span>
          )}
          {job.jobOpening.contractPeriod.name}
        </div>
      )}
    </>
  );
};

const JobFormField = ({ job, internalName, withIcon = false, icon }: FormFieldProperties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;

  const formFieldAnswerList = job.jobOpening.formFieldAnswers?.filter(
    (formFieldAnswer) => formFieldAnswer.internalName === internalName,
  );

  return (
    <>
      {formFieldAnswerList && (formFieldAnswerList[0]?.answers || formFieldAnswerList[0]?.answer) && (
        <ul className="dvinci-list-inline dvinci-list-form-field">
          {(withIcon || showIcons) && (
            <li className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faPuzzlePiece} fixedWidth />
            </li>
          )}
          {formFieldAnswerList.map((formFieldAnswer) => (
            <>
              {formFieldAnswer.answers && (
                <>
                  {formFieldAnswer.answers.map((formFieldMultipleChoiceAnswer) => (
                    <li key={formFieldMultipleChoiceAnswer.internalName}>{formFieldMultipleChoiceAnswer.answer}</li>
                  ))}
                </>
              )}
              {formFieldAnswer.answer && <li key={formFieldAnswer.id}>{formFieldAnswer.answer}</li>}
            </>
          ))}
        </ul>
      )}
    </>
  );
};

const JobFlagNew = ({ job }: { job: Job }) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { flagAsNew, flagAsNewEnabled } = config.config.ui;
  const { sortDate } = job;
  return (
    <>
      {flagAsNewEnabled && dayjs().diff(sortDate, 'days') <= flagAsNew && (
        <span className="badge dvinci-flag-new">{t`New`}</span>
      )}
    </>
  );
};

const JobOrgUnit = ({ job, withIcon = false, icon }: Properties) => {
  const state = useContext(StateContext);
  const { config } = state;
  const { showIcons } = config.config.ui;
  const { enabled } = config.config.cardContent.orgUnit;

  return (
    <>
      {enabled && job.jobOpening.orgUnit && (
        <ul className="dvinci-list-inline dvinci-list-orgunit">
          {(withIcon || showIcons) && (
            <li className="dvinci-icon-wrapper">
              <FontAwesomeIcon icon={icon || faMapPin} fixedWidth />
            </li>
          )}
          <li key={job.jobOpening.orgUnit.id}>{job.jobOpening.orgUnit.name}</li>
        </ul>
      )}
    </>
  );
};

export {
  JobCategoryList,
  JobLocationList,
  JobTargetGroupList,
  JobStartDate,
  JobEndDate,
  JobWorkingTimeList,
  JobCompany,
  JobEarliestEntryDate,
  JobContractPeriod,
  JobFormField,
  JobFlagNew,
  JobOrgUnit,
};
